import { useCallback, useState } from "react";

export const ORG_TREE_ZOOM_MIN = 0.5;
export const ORG_TREE_ZOOM_MAX = 1.2;
const ZOOM_STEP = 0.1;
// Fitting never enlarges a tree past its natural size: a small org stays at
// 100% rather than being blown up. Zoom in still goes to 120%.
const FIT_ZOOM_MAX = 1;

// The one zoom level at which every given tree fits its card's width, so the
// shared zoom fits both cards at once. Each measurement is
// `{ contentWidth, viewportWidth }` (the tree's unscaled width and its card's
// visible width); null entries (a card that isn't showing a tree) are
// skipped. Width only: an org's breadth is what forces sideways scrolling,
// while a deep org scrolling vertically is expected. Clamped between
// ORG_TREE_ZOOM_MIN (a very wide org stops shrinking at 50% and scrolls
// from there) and FIT_ZOOM_MAX.
export const computeFitZoom = (measurements) => {
  const ratios = measurements
    .filter((m) => m && m.contentWidth > 0 && m.viewportWidth > 0)
    .map((m) => m.viewportWidth / m.contentWidth);
  if (!ratios.length) return FIT_ZOOM_MAX;
  // Floored to a whole percent so rounding can't push a fitted tree a pixel
  // past its card and bring back a scrollbar. The epsilon stops float error
  // (0.57 * 100 = 56.999…) costing an exact ratio a whole percent.
  const fit = Math.floor(Math.min(...ratios) * 100 + 1e-9) / 100;
  return Math.min(FIT_ZOOM_MAX, Math.max(ORG_TREE_ZOOM_MIN, fit));
};

// Drives the Current/Proposed OrgTreeCard pair from one shared viewport so
// zooming or panning either side moves both in lockstep — two independently
// scrolled/zoomed trees made visually comparing "what changed" nearly
// impossible once either side drifted out of alignment.
//
// Zoom is a single shared number (both cards render at the same %). Scroll
// is synced as a *fraction* of each card's own scrollable range, not a raw
// pixel offset — the Current and Proposed trees are rarely the same size
// (added/removed roles), so mirroring fractions keeps both views pointed at
// the same relative position in their own tree instead of clipping one side
// against the other's absolute pixel offset. `token` is bumped on every
// user-driven scroll so a card can tell "a new sync arrived" apart from
// "this object was recreated but nothing actually changed".
//
// The default view is "fitted": `fitToCards` sets the zoom at which both
// trees fit their cards (see computeFitZoom) and re-centers each card. The
// workspace calls it with fresh measurements whenever Org View opens, its
// layout changes, or the user presses Reset. Expand all / Collapse all only
// re-center (recenter), keeping the zoom. Only the zoom buttons animate
// (`animateZoom`); a fit lands at once, so the charts never visibly slide
// from an old zoom to the fitted one.
//
// "At default" (the Reset button's disabled state) is re-derived from the
// live zoom value plus whichever card last reported a scroll telling us
// whether IT is currently back at its own centered default (see
// OrgTreeCard's handleScroll) — not a one-way "has this ever been touched"
// flag, so zooming or scrolling back to exactly the default re-disables
// Reset instead of leaving it stuck enabled.
const useOrgTreeViewportSync = () => {
  const [fitZoom, setFitZoom] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [animateZoom, setAnimateZoom] = useState(false);
  const [hasScrolledAway, setHasScrolledAway] = useState(false);
  const [scrollSync, setScrollSync] = useState({ xFrac: null, yFrac: null, token: 0 });
  const [resetToken, setResetToken] = useState(0);

  const zoomIn = useCallback(() => {
    setAnimateZoom(true);
    setZoom((z) => Math.min(ORG_TREE_ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  }, []);

  const zoomOut = useCallback(() => {
    setAnimateZoom(true);
    setZoom((z) => Math.max(ORG_TREE_ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  }, []);

  // Clears the scroll-sync channel (so a late-arriving scroll event from
  // before this can't re-broadcast a stale position) and asks both cards
  // (via resetToken) to re-center their own scroll position — each card
  // measures its own default center, so this can't just set one shared
  // scroll value.
  const recenter = useCallback(() => {
    setHasScrolledAway(false);
    setScrollSync((s) => ({ xFrac: null, yFrac: null, token: s.token + 1 }));
    setResetToken((t) => t + 1);
  }, []);

  // `measurements` come from each card's measureFit() (see OrgTreeCard).
  // Applies the fitted zoom and re-centers both cards.
  const fitToCards = useCallback(
    (measurements) => {
      const fit = computeFitZoom(measurements);
      setFitZoom(fit);
      setZoom(fit);
      setAnimateZoom(false);
      recenter();
    },
    [recenter]
  );

  // Called by whichever card the user just scrolled/panned — broadcasts the
  // new position (as a fraction of that card's own scrollable range) to the
  // other card, plus whether that card is currently back at its own centered
  // default (used only to drive the Reset button's disabled state).
  // `sourceVariant` (the reporting OrgTreeCard's own `variant` prop) rides
  // along so that same card can skip re-applying this sync back onto itself
  // — see OrgTreeCard's sync-apply effect for why that self-echo was making
  // both scrollbars jitter.
  const reportUserScroll = useCallback((xFrac, yFrac, atDefault, sourceVariant) => {
    setScrollSync((s) => ({ xFrac, yFrac, token: s.token + 1, sourceVariant }));
    setHasScrolledAway(!atDefault);
  }, []);

  return {
    zoom,
    zoomIn,
    zoomOut,
    fitToCards,
    recenter,
    animateZoom,
    isAtDefaultView: zoom === fitZoom && !hasScrolledAway,
    scrollSync,
    reportUserScroll,
    resetToken,
    zoomMin: ORG_TREE_ZOOM_MIN,
    zoomMax: ORG_TREE_ZOOM_MAX,
  };
};

export default useOrgTreeViewportSync;
